<div class="role16-420000-form-container">
  <p-toast></p-toast>
  <p-confirmDialog></p-confirmDialog>

  <app-reload-banner [visible]="reloadPromptDismissed()" (reload)="onReload()" />

  <app-sheet-loader [visible]="isLoading" />

  <!-- Form Content -->
  <div *ngIf="!isLoading && schema && form">
    <p-card class="form-card">
      <!-- Form Header -->
      <div class="form-header">
        <h2>{{ formTitle }}</h2>
        <p class="form-subtitle">{{ schema.description }}</p>
        <div class="field-count">
          <i class="pi pi-list"></i>
          <span>{{ schema.controls.length }} fields</span>
        </div>

        <!-- Consolidated/Comparative Notice -->
        <div *ngIf="isConsolidated" class="consolidated-notice">
          <i class="pi pi-info-circle"></i>
          <span>Consolidated financial statements - showing Group and Company columns</span>
        </div>
        <div *ngIf="hasPreviousYear && !isConsolidated" class="consolidated-notice">
          <i class="pi pi-info-circle"></i>
          <span>Comparative information presented - showing Current and Previous Year columns</span>
        </div>
        <div *ngIf="isConsolidated && hasPreviousYear" class="consolidated-notice">
          <i class="pi pi-info-circle"></i>
          <span>Showing 4 columns: Group (Current & Previous) and Company (Current & Previous)</span>
        </div>
      </div>

      <!-- Financial Statement Table -->
      <div class="financial-statement-table-container">
        <form [formGroup]="form">
          <table class="financial-statement-table"
          appFieldLinkTarget
          fieldLinkSheet="statement-of-comprehensive-income-before-tax"
          [fieldLinkControls]="schema.controls"
        >
            <thead>
              <tr>
                <th class="description-column">Description</th>

                <!-- Scenario 1: 4 columns (Consolidated + Previous Year) -->
                <ng-container *ngIf="isConsolidated && hasPreviousYear">
                  <th class="amount-column">Group<br/><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
                  <th class="amount-column">Group<br/><span class="period-header">{{ periodHeaderPrevious }}</span><br/>{{ currencyUnit }}</th>
                  <th class="amount-column">Company<br/><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
                  <th class="amount-column">Company<br/><span class="period-header">{{ periodHeaderPrevious }}</span><br/>{{ currencyUnit }}</th>
                </ng-container>

                <!-- Scenario 2: 2 columns (Consolidated only, no previous year) -->
                <ng-container *ngIf="isConsolidated && !hasPreviousYear">
                  <th class="amount-column">Group<br/><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
                  <th class="amount-column">Company<br/><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
                </ng-container>

                <!-- Scenario 3: 2 columns (Previous year only, not consolidated) -->
                <ng-container *ngIf="!isConsolidated && hasPreviousYear">
                  <th class="amount-column"><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
                  <th class="amount-column"><span class="period-header">{{ periodHeaderPrevious }}</span><br/>{{ currencyUnit }}</th>
                </ng-container>

                <!-- Scenario 4: 1 column (Neither consolidated nor previous year) -->
                <ng-container *ngIf="!isConsolidated && !hasPreviousYear">
                  <th class="amount-column"><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
                </ng-container>

                <th class="validation-error-column">Error Description</th>
              </tr>
            </thead>

            <tbody>
              <ng-container *ngFor="let control of schema.controls">
                <!-- Section Headers -->
                <tr *ngIf="isHeader(control)" [ngClass]="getHeaderClass(control)">
                  <td [attr.colspan]="getColumnCount() + 2">
                    <h3>{{ control.label }}</h3>
                  </td>
                </tr>

                <!-- Monetary Input Fields (Dual Column Support) -->
                <tr *ngIf="!isHeader(control) && hasDualColumn(control)" [attr.data-qname]="control.qname" [ngClass]="getRowClass(control)">
                  <td class="description-column">
                    {{ control.label }}<app-required-marker [when]="isFieldRequired(control)" />
                    <i *ngIf="control.ui.note && fieldNoteTooltipsEnabled()"
                       class="pi pi-info-circle"
                       [pTooltip]="control.ui.note"
                       tooltipPosition="right"
                       style="margin-left: 0.5rem; color: #6b7280; font-size: 0.875rem;"></i>
                    <span *ngIf="isCalculated(control)" style="color: #4338ca; font-weight: 600; margin-left: 0.5rem;">(Calculated)</span>
                  </td>

                  <!-- Scenario 1: 4 columns -->
                  <ng-container *ngIf="isConsolidated && hasPreviousYear">
                    <td class="amount-column" [ngClass]="{'calculated-field': isCalculated(control)}">
                      <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                        [formControlName]="getXbrlControlName(control.qname, 'Group', 'Current')"
                        mode="decimal"
                        [minFractionDigits]="numericPolicy(control).decimals"
                        [maxFractionDigits]="numericPolicy(control).decimals"
                        [useGrouping]="numericPolicy(control).grouping"
                        [prefix]="control.ui.currency === 'MYR' ? 'RM ' : ''"
                        [placeholder]="control | placeholder: '0.00'"
                        [style]="{'width': '100%'}"
                        [disabled]="isCalculated(control)"
                      ></p-inputnumber>
                    </td>
                    <td class="amount-column" [ngClass]="{'calculated-field': isCalculated(control)}">
                      <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                        [formControlName]="getXbrlControlName(control.qname, 'Group', 'Previous')"
                        mode="decimal"
                        [minFractionDigits]="numericPolicy(control).decimals"
                        [maxFractionDigits]="numericPolicy(control).decimals"
                        [useGrouping]="numericPolicy(control).grouping"
                        [prefix]="control.ui.currency === 'MYR' ? 'RM ' : ''"
                        [placeholder]="control | placeholder: '0.00'"
                        [style]="{'width': '100%'}"
                        [disabled]="isCalculated(control)"
                      ></p-inputnumber>
                    </td>
                    <td class="amount-column" [ngClass]="{'calculated-field': isCalculated(control)}">
                      <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                        [formControlName]="getXbrlControlName(control.qname, 'Company', 'Current')"
                        mode="decimal"
                        [minFractionDigits]="numericPolicy(control).decimals"
                        [maxFractionDigits]="numericPolicy(control).decimals"
                        [useGrouping]="numericPolicy(control).grouping"
                        [prefix]="control.ui.currency === 'MYR' ? 'RM ' : ''"
                        [placeholder]="control | placeholder: '0.00'"
                        [style]="{'width': '100%'}"
                        [disabled]="isCalculated(control)"
                      ></p-inputnumber>
                    </td>
                    <td class="amount-column" [ngClass]="{'calculated-field': isCalculated(control)}">
                      <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                        [formControlName]="getXbrlControlName(control.qname, 'Company', 'Previous')"
                        mode="decimal"
                        [minFractionDigits]="numericPolicy(control).decimals"
                        [maxFractionDigits]="numericPolicy(control).decimals"
                        [useGrouping]="numericPolicy(control).grouping"
                        [prefix]="control.ui.currency === 'MYR' ? 'RM ' : ''"
                        [placeholder]="control | placeholder: '0.00'"
                        [style]="{'width': '100%'}"
                        [disabled]="isCalculated(control)"
                      ></p-inputnumber>
                    </td>
                  </ng-container>

                  <!-- Scenario 2: 2 columns (Consolidated only) -->
                  <ng-container *ngIf="isConsolidated && !hasPreviousYear">
                    <td class="amount-column" [ngClass]="{'calculated-field': isCalculated(control)}">
                      <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                        [formControlName]="getXbrlControlName(control.qname, 'Group', 'Current')"
                        mode="decimal"
                        [minFractionDigits]="numericPolicy(control).decimals"
                        [maxFractionDigits]="numericPolicy(control).decimals"
                        [useGrouping]="numericPolicy(control).grouping"
                        [prefix]="control.ui.currency === 'MYR' ? 'RM ' : ''"
                        [placeholder]="control | placeholder: '0.00'"
                        [style]="{'width': '100%'}"
                        [disabled]="isCalculated(control)"
                      ></p-inputnumber>
                    </td>
                    <td class="amount-column" [ngClass]="{'calculated-field': isCalculated(control)}">
                      <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                        [formControlName]="getXbrlControlName(control.qname, 'Company', 'Current')"
                        mode="decimal"
                        [minFractionDigits]="numericPolicy(control).decimals"
                        [maxFractionDigits]="numericPolicy(control).decimals"
                        [useGrouping]="numericPolicy(control).grouping"
                        [prefix]="control.ui.currency === 'MYR' ? 'RM ' : ''"
                        [placeholder]="control | placeholder: '0.00'"
                        [style]="{'width': '100%'}"
                        [disabled]="isCalculated(control)"
                      ></p-inputnumber>
                    </td>
                  </ng-container>

                  <!-- Scenario 3: 2 columns (Previous year only) -->
                  <ng-container *ngIf="!isConsolidated && hasPreviousYear">
                    <td class="amount-column" [ngClass]="{'calculated-field': isCalculated(control)}">
                      <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                        [formControlName]="control.qname + 'Current'"
                        mode="decimal"
                        [minFractionDigits]="numericPolicy(control).decimals"
                        [maxFractionDigits]="numericPolicy(control).decimals"
                        [useGrouping]="numericPolicy(control).grouping"
                        [prefix]="control.ui.currency === 'MYR' ? 'RM ' : ''"
                        [placeholder]="control | placeholder: '0.00'"
                        [style]="{'width': '100%'}"
                        [disabled]="isCalculated(control)"
                      ></p-inputnumber>
                    </td>
                    <td class="amount-column" [ngClass]="{'calculated-field': isCalculated(control)}">
                      <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                        [formControlName]="control.qname + 'Previous'"
                        mode="decimal"
                        [minFractionDigits]="numericPolicy(control).decimals"
                        [maxFractionDigits]="numericPolicy(control).decimals"
                        [useGrouping]="numericPolicy(control).grouping"
                        [prefix]="control.ui.currency === 'MYR' ? 'RM ' : ''"
                        [placeholder]="control | placeholder: '0.00'"
                        [style]="{'width': '100%'}"
                        [disabled]="isCalculated(control)"
                      ></p-inputnumber>
                    </td>
                  </ng-container>

                  <!-- Scenario 4: 1 column (Neither) -->
                  <ng-container *ngIf="!isConsolidated && !hasPreviousYear">
                    <td class="amount-column" [ngClass]="{'calculated-field': isCalculated(control)}">
                      <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                        [formControlName]="control.qname + 'Current'"
                        mode="decimal"
                        [minFractionDigits]="numericPolicy(control).decimals"
                        [maxFractionDigits]="numericPolicy(control).decimals"
                        [useGrouping]="numericPolicy(control).grouping"
                        [prefix]="control.ui.currency === 'MYR' ? 'RM ' : ''"
                        [placeholder]="control | placeholder: '0.00'"
                        [style]="{'width': '100%'}"
                        [disabled]="isCalculated(control)"
                      ></p-inputnumber>
                    </td>
                  </ng-container>

                  <td class="validation-error-column"><span [innerHTML]="getRowErrorDescription(control)"></span></td>
                </tr>
              </ng-container>
            </tbody>
          </table>
        </form>
      </div>

      <!-- Form Actions -->
      <div class="form-actions">
        <p-button
          label="🎯 Auto-fill Demo"
          (onClick)="autoFillDemo()"
          severity="secondary"
          [outlined]="true"
          size="small"
        ></p-button>

        <p-button
          label="Clear"
          icon="pi pi-trash"
          (onClick)="clearForm()"
          severity="danger"
          [outlined]="true"
          size="small"
        ></p-button>

        <div class="flex flex-col items-end gap-1">
          <p-button
            label="Submit"
            icon="pi pi-check"
            (onClick)="submitForm()"
            severity="success"
            size="small"
            [disabled]="reloadPromptDismissed()"
          ></p-button>
          <small
            *ngIf="reloadPromptDismissed()"
            class="text-xs text-amber-700"
          >
            Please refresh the page to obtain the latest version of this sheet.
          </small>
        </div>
      </div>
    </p-card>
  </div>

  <app-reload-prompt
    [visible]="showReloadPrompt()"
    mode="return-or-stay"
    title="Filing regenerated"
    message="Core settings were just submitted. The data on this page is being regenerated — return to files list, or stay on this view (it may be out of date)."
    (reload)="onReload()"
    (dismiss)="onDismiss()"
    (returnToFiles)="onReturnToFiles()"
  ></app-reload-prompt>
</div>
